import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../utils/api';

// -- Types --------------------------------------------------------------------

interface StrategyOption {
  id: number;
  name: string;
}

interface CustomQuestion {
  label: string;
  value: string;
  required: boolean;
}

// Shape returned by GET /ext/content_requests/new/:shareToken.json
// (External::ContentRequestsController#new — companion BE PR).
interface ContentRequestForm {
  // Numeric FK the create endpoint needs as content_request_form_id (not the share token).
  id: number;
  brandProfileId: number;
  shareToken: string;
  deleted: boolean;
  // Form config (parity-audit-r3, 3-SZ-2): the form's own name + publication name,
  // instructions, file, and the audience/pillar/custom-question inputs classic's
  // NewContentRequest form rendered.
  name?: string;
  publicationName?: string;
  instructions?: string | null;
  fileUrl?: string | null;
  includeAudience?: boolean;
  includePillar?: boolean;
  audiences?: StrategyOption[];
  pillars?: StrategyOption[];
  questions?: CustomQuestion[];
}

// Local form state for the external submitter.
interface SubmissionState {
  projectTitle: string;
  description: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  dueDate: string;
  audienceId: string;
  pillarId: string;
  termsAccepted: boolean;
}

// -- Styled Components (mirrors ExternalContentRequestPage) --------------------

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5f6f8;
  padding: 40px 20px;
`;

const Card = styled.div`
  max-width: 720px;
  margin: 0 auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 32px;
`;

const Header = styled.div`
  margin-bottom: 24px;
  border-bottom: 1px solid #e8eaed;
  padding-bottom: 16px;
`;

const PublicationName = styled.div`
  font-size: 13px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 8px 0 0;
  line-height: 1.5;
`;

const DeletedBanner = styled.div`
  background: #fef3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;
  color: #856404;
  font-size: 14px;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const RequiredMark = styled.span`
  color: #dc2626;
  margin-left: 2px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 15px;
  font-family: inherit;
  color: #1a1a2e;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 15px;
  font-family: inherit;
  color: #1a1a2e;
  box-sizing: border-box;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }
`;

const Instructions = styled.p`
  font-size: 15px;
  color: #4b5563;
  line-height: 1.5;
  white-space: pre-line;
  margin: 0 0 8px;
`;

const FileLink = styled.a`
  font-size: 15px;
  color: #2563eb;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 15px;
  font-family: inherit;
  color: #1a1a2e;
  resize: vertical;
  min-height: 120px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  & > ${Section} {
    flex: 1;
    min-width: 200px;
  }
`;

const TermsRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  margin-bottom: 20px;
  cursor: pointer;
  line-height: 1.5;
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 28px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorBanner = styled.div`
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #b91c1c;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 40px 0;
`;

const SuccessTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: #1a7f37;
  margin: 0 0 8px;
`;

const SuccessText = styled.p`
  font-size: 15px;
  color: #4b5563;
  margin: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  font-size: 16px;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  max-width: 720px;
  margin: 60px auto;
  text-align: center;
  color: #dc2626;
  font-size: 16px;
`;

// -- Component ----------------------------------------------------------------

const emptySubmission = (): SubmissionState => ({
  projectTitle: '',
  description: '',
  firstName: '',
  lastName: '',
  email: '',
  department: '',
  dueDate: '',
  audienceId: '',
  pillarId: '',
  termsAccepted: false,
});

const NewExternalContentRequestPage = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  // The currently-loaded form token. An in-flight submit captures the token it started
  // with; on resolve we compare against this ref (always the live URL token) and drop
  // the result if the user navigated to a different external form meanwhile.
  const activeTokenRef = useRef<string | undefined>(shareToken);
  const [form, setForm] = useState<ContentRequestForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [submission, setSubmission] = useState<SubmissionState>(emptySubmission());
  // Custom-question answers, keyed by question index (parity-audit-r3, 3-SZ-2).
  const [questionAnswers, setQuestionAnswers] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!shareToken) return;
    // Reset ALL per-form state before loading a new token (SPA nav between external
    // forms). Carrying any of it over would: pair the prior form's answers with the
    // next form's questions[] labels (questionAnswers, index-keyed), submit the prior
    // form's audience/pillar IDs + text fields (submission), keep showing the success
    // screen instead of the new form (submitted), show a stale error (submitError), or
    // leave the submit control disabled mid-"Submitting…" from a prior in-flight POST
    // (submitting).
    activeTokenRef.current = shareToken;
    setLoading(true);
    setLoadError(null);
    setForm(null);
    setQuestionAnswers({});
    setSubmission(emptySubmission());
    setSubmitted(false);
    setSubmitError(null);
    setSubmitting(false);
    api
      .get(`/ext/content_requests/new/${shareToken}.json`)
      .then((res) => {
        setForm(res.data.contentRequestForm);
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setLoadError('This content request form could not be found.');
        } else {
          setLoadError('Something went wrong loading this content request form.');
        }
      })
      .finally(() => setLoading(false));
  }, [shareToken]);

  const updateField = <K extends keyof SubmissionState>(
    field: K,
    value: SubmissionState[K],
  ) => {
    setSubmission((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !shareToken) return;

    // Token this submission belongs to; compared against activeTokenRef after the POST
    // resolves so a slow response can't redirect/error/succeed onto a different form the
    // user navigated to meanwhile.
    const submitToken = shareToken;

    // Guard the numeric form id: the create endpoint's content_request_form_id is a numeric FK.
    // If new.json didn't return `id` (e.g. the companion contently-contently change hasn't deployed
    // yet), bail with an error instead of POSTing the literal "undefined" and 500ing after the user
    // filled out the whole form. (eng-maintenance#21234)
    if (form.id === undefined || form.id === null) {
      setSubmitError('Something went wrong loading this form. Please refresh and try again.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    // Custom questions the form defines (parity-audit-r3, 3-SZ-2): new.json now returns
    // the form's questions; pair each with the submitter's answer. Matches the legacy
    // Grape `questions` contract — an array of { label, value, required }.
    const questions: CustomQuestion[] = (form.questions || []).map((q, idx) => ({
      label: q.label,
      required: q.required,
      value: questionAnswers[idx] || '',
    }));

    // Post to the legacy Grape endpoint the pre-migration form used:
    // POST /api/v1/external/content_requests (InternalAPI::V1::External, intact in master). The
    // migration pointed this at /ext/content_requests — a Rails route whose controller has no
    // #create action — so submission 404'd, and it also sent the share token as
    // content_request_form_id (the FK is numeric) which 500'd. Use the real endpoint + the form's
    // numeric id (now exposed by new.json via the companion contently-contently PR).
    // eng-maintenance#21234 E2E.
    const CREATE_URL = '/api/v1/external/content_requests';
    try {
      const res = await api.post(CREATE_URL, {
        content_request_form_id: String(form.id),
        project_title: submission.projectTitle,
        description: submission.description,
        external_user: {
          first_name: submission.firstName,
          last_name: submission.lastName,
          email: submission.email,
        },
        department: submission.department || undefined,
        due_date: submission.dueDate || undefined,
        audience_id: submission.audienceId || undefined,
        pillar_id: submission.pillarId || undefined,
        questions,
        terms_and_conditions: submission.termsAccepted,
      });

      // Classic redirected to the SUBMITTED request's own view on success
      // (window.location.replace('/ext/content_requests/:requestShareToken')). The POST returns
      // the ContentRequest as a JSON:API envelope (ContentRequestSerializer, camelCase) — its
      // `shareToken` is the REQUEST's token (minted on save), distinct from the form's share token
      // in the URL. Navigate there to restore parity; fall back to the in-page success card if the
      // token is absent for any reason.
      // Drop the result if the user navigated to a different external form while the
      // POST was in flight — the new form already reset its own state in the effect.
      if (activeTokenRef.current !== submitToken) return;

      const requestShareToken = res?.data?.data?.attributes?.shareToken;
      if (requestShareToken) {
        navigate(`/ext/content_requests/${requestShareToken}`);
        return;
      }
      setSubmitted(true);
    } catch (err: unknown) {
      if (activeTokenRef.current !== submitToken) return;
      const axiosErr = err as { response?: { data?: { errors?: unknown } } };
      const errors = axiosErr.response?.data?.errors;
      const message =
        typeof errors === 'string'
          ? errors
          : 'We could not submit your content request. Please check the form and try again.';
      setSubmitError(message);
    } finally {
      // Only clear the spinner for the form that's still showing — a navigated-away
      // submission must not touch the new form's freshly-reset submitting flag.
      if (activeTokenRef.current === submitToken) {
        setSubmitting(false);
      }
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>Loading...</LoadingContainer>
      </PageContainer>
    );
  }

  if (loadError || !form) {
    return (
      <PageContainer>
        <ErrorContainer>{loadError || 'Content request form not found.'}</ErrorContainer>
      </PageContainer>
    );
  }

  // The form has been deleted — it no longer accepts submissions. The legacy
  // HAML rendered a `preview-mode` read-only state in this case.
  if (form.deleted) {
    return (
      <PageContainer>
        <Card>
          <Header>
            <PublicationName>Content Request</PublicationName>
            <Title>This form is no longer accepting submissions</Title>
          </Header>
          <DeletedBanner>
            This content request form has been removed and can no longer receive new
            submissions.
          </DeletedBanner>
        </Card>
      </PageContainer>
    );
  }

  if (submitted) {
    return (
      <PageContainer>
        <Card>
          <SuccessContainer>
            <SuccessTitle>Thank you!</SuccessTitle>
            <SuccessText>
              Your content request has been submitted. The team will review it shortly.
            </SuccessText>
          </SuccessContainer>
        </Card>
      </PageContainer>
    );
  }

  // Required custom questions must be answered (parity-audit-r3, 3-SZ-2) — classic's
  // AnswerField enforced `required` per question before allowing submit.
  const requiredQuestionsAnswered = (form.questions || []).every(
    (q, idx) => !q.required || (questionAnswers[idx] || '').trim() !== '',
  );

  const canSubmit =
    submission.projectTitle.trim() !== '' &&
    submission.description.trim() !== '' &&
    submission.firstName.trim() !== '' &&
    submission.lastName.trim() !== '' &&
    submission.email.trim() !== '' &&
    submission.termsAccepted &&
    requiredQuestionsAnswered &&
    !submitting;

  return (
    <PageContainer>
      <Card>
        <Header>
          <PublicationName>{form.publicationName || 'Content Request'}</PublicationName>
          <Title>{form.name || 'Submit a content request'}</Title>
          <Subtitle>
            Fill out the form below to submit your content request. Fields marked with an
            asterisk are required.
          </Subtitle>
          {form.instructions && <Instructions>{form.instructions}</Instructions>}
          {form.fileUrl && (
            <FileLink href={form.fileUrl} target="_blank" rel="noopener noreferrer">
              Download attached materials
            </FileLink>
          )}
        </Header>

        {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

        <form onSubmit={handleSubmit} noValidate>
          <Section>
            <Label htmlFor="cr-project-title">
              Project Title<RequiredMark>*</RequiredMark>
            </Label>
            <TextInput
              id="cr-project-title"
              type="text"
              value={submission.projectTitle}
              maxLength={255}
              onChange={(e) => updateField('projectTitle', e.target.value)}
              placeholder="Give your request a title"
              required
            />
          </Section>

          <Row>
            <Section>
              <Label htmlFor="cr-first-name">
                First Name<RequiredMark>*</RequiredMark>
              </Label>
              <TextInput
                id="cr-first-name"
                type="text"
                value={submission.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                required
              />
            </Section>
            <Section>
              <Label htmlFor="cr-last-name">
                Last Name<RequiredMark>*</RequiredMark>
              </Label>
              <TextInput
                id="cr-last-name"
                type="text"
                value={submission.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                required
              />
            </Section>
          </Row>

          <Section>
            <Label htmlFor="cr-email">
              Email<RequiredMark>*</RequiredMark>
            </Label>
            <TextInput
              id="cr-email"
              type="email"
              value={submission.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="you@example.com"
              required
            />
          </Section>

          <Section>
            <Label htmlFor="cr-department">Department</Label>
            <TextInput
              id="cr-department"
              type="text"
              value={submission.department}
              onChange={(e) => updateField('department', e.target.value)}
            />
          </Section>

          <Section>
            <Label htmlFor="cr-due-date">Due Date</Label>
            <TextInput
              id="cr-due-date"
              type="date"
              value={submission.dueDate}
              onChange={(e) => updateField('dueDate', e.target.value)}
            />
          </Section>

          <Section>
            <Label htmlFor="cr-description">
              Description<RequiredMark>*</RequiredMark>
            </Label>
            <Textarea
              id="cr-description"
              value={submission.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Describe what you need..."
              required
            />
          </Section>

          {/* Audience / Pillar selectors (parity-audit-r3, 3-SZ-2) — rendered only when the
              form enables them, mirroring classic's include_audience / include_pillar gating. */}
          {form.includeAudience && (form.audiences?.length ?? 0) > 0 && (
            <Section>
              <Label htmlFor="cr-audience">Audience</Label>
              <Select
                id="cr-audience"
                value={submission.audienceId}
                onChange={(e) => updateField('audienceId', e.target.value)}
              >
                <option value="">Select an audience</option>
                {form.audiences?.map((a) => (
                  <option key={a.id} value={String(a.id)}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </Section>
          )}

          {form.includePillar && (form.pillars?.length ?? 0) > 0 && (
            <Section>
              <Label htmlFor="cr-pillar">Pillar</Label>
              <Select
                id="cr-pillar"
                value={submission.pillarId}
                onChange={(e) => updateField('pillarId', e.target.value)}
              >
                <option value="">Select a pillar</option>
                {form.pillars?.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Section>
          )}

          {/* Custom questions the form defines (parity-audit-r3, 3-SZ-2). Classic rendered
              these via a FieldArray of AnswerField; each answer feeds the submitted
              questions[] array of { label, value, required }. */}
          {(form.questions?.length ?? 0) > 0 &&
            form.questions?.map((q, idx) => (
              <Section key={`${q.label}-${idx}`}>
                <Label htmlFor={`cr-question-${idx}`}>
                  {q.label}
                  {q.required && <RequiredMark>*</RequiredMark>}
                </Label>
                <TextInput
                  id={`cr-question-${idx}`}
                  type="text"
                  value={questionAnswers[idx] || ''}
                  required={q.required}
                  onChange={(e) =>
                    setQuestionAnswers((prev) => ({ ...prev, [idx]: e.target.value }))
                  }
                />
              </Section>
            ))}

          {/* No attachment field: the pre-migration external content-request form had no file-upload
              input (it only showed the FORM's own attachment as a read-only download link). The
              migration added one, but it POSTed raw multipart that the Grape endpoint silently
              dropped (it expects an ActiveStorage signed_id). Removed for parity. (#21234) */}

          <TermsRow>
            <input
              type="checkbox"
              checked={submission.termsAccepted}
              onChange={(e) => updateField('termsAccepted', e.target.checked)}
            />
            <span>
              I agree to the terms and conditions.<RequiredMark>*</RequiredMark>
            </span>
          </TermsRow>

          <SubmitButton type="submit" disabled={!canSubmit}>
            {submitting ? 'Submitting...' : 'Submit Request'}
          </SubmitButton>
        </form>
      </Card>
    </PageContainer>
  );
};

export { NewExternalContentRequestPage };
export default NewExternalContentRequestPage;
